"use client";

import React from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Switch,
  Slider,
  Select,
  Button,
} from "@/ui";
import { colors, spacing, textStyles } from "@/ui/tokens";
import {
  Settings,
  Palette,
} from "@mui/icons-material";
import { useSOLAFloating } from "./context";

export function SettingsPanel() {
  const { state, updatePreferences } = useSOLAFloating();
  const { preferences } = state;

  const handlePreferenceChange = (key, value) => {
    updatePreferences({ [key]: value });
  };

  return (
    <div style={{ padding: spacing[4], height: "100%", overflow: "auto" }}>
      <h3
        style={{
          ...textStyles.h3,
          marginBottom: spacing[4],
          display: "flex",
          alignItems: "center",
          gap: spacing[2],
        }}
      >
        <Settings style={{ fontSize: 20 }} /> Settings
      </h3>

      <Accordion type="single" defaultValue={["appearance"]}>
        {/* Appearance Settings */}
        <AccordionItem value="appearance">
          <AccordionTrigger>
            <span style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
              <Palette style={{ fontSize: 20 }} />
              Appearance
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
              <Select
                label="Theme"
                value={preferences.theme}
                onChange={(value) => handlePreferenceChange("theme", value)}
                options={[
                  { value: "light", label: "Light" },
                  { value: "dark", label: "Dark" },
                  { value: "auto", label: "Auto" },
                ]}
              />

              <Select
                label="Size"
                value={preferences.size}
                onChange={(value) => handlePreferenceChange("size", value)}
                options={[
                  { value: "compact", label: "Compact" },
                  { value: "normal", label: "Normal" },
                  { value: "large", label: "Large" },
                ]}
              />

              <Slider
                label={`Opacity: ${Math.round(preferences.opacity * 100)}%`}
                value={preferences.opacity * 100}
                onChange={(value) => handlePreferenceChange("opacity", value / 100)}
                min={50}
                max={100}
                step={5}
                showValue
                formatValue={(v) => `${Math.round(v)}%`}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Behavior Settings */}
        <AccordionItem value="behavior">
          <AccordionTrigger>Behavior</AccordionTrigger>
          <AccordionContent>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
              <Switch
                checked={preferences.autoMinimize}
                onChange={(checked) => handlePreferenceChange("autoMinimize", checked)}
                label="Auto-minimize when inactive"
              />

              <Switch
                checked={preferences.showNotifications}
                onChange={(checked) => handlePreferenceChange("showNotifications", checked)}
                label="Show notifications"
              />

              <Switch
                checked={preferences.contextAwareness}
                onChange={(checked) => handlePreferenceChange("contextAwareness", checked)}
                label="Context awareness"
              />

              <Switch
                checked={preferences.proactiveSuggestions}
                onChange={(checked) => handlePreferenceChange("proactiveSuggestions", checked)}
                label="Proactive suggestions"
              />

              <Switch
                checked={preferences.keyboardShortcuts}
                onChange={(checked) => handlePreferenceChange("keyboardShortcuts", checked)}
                label="Keyboard shortcuts"
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Reset Button */}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          updatePreferences({
            theme: "auto",
            position: "bottom-right",
            size: "normal",
            opacity: 1,
            autoMinimize: false,
            showNotifications: true,
            enableSounds: false,
            enableVoice: false,
            contextAwareness: true,
            proactiveSuggestions: true,
            saveConversations: true,
            shareAnalytics: false,
            maxConversations: 10,
            animationSpeed: "normal",
            keyboardShortcuts: true,
          });
        }}
        style={{ marginTop: spacing[4] }}
      >
        Reset to Defaults
      </Button>
    </div>
  );
}
